import { Link } from 'react-router-dom';
import { X, Bell, CheckCircle, Truck, Calendar, WashingMachine, Scissors } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import type { NotificationItem } from '@/lib/types';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

function iconFor(type: NotificationItem['type']) {
  switch (type) {
    case 'service-pickup':
      return WashingMachine;
    case 'pending-delivery':
      return Truck;
    case 'appointment':
      return Calendar;
    case 'tailor-pending':
    case 'tailor-return':
      return Scissors;
    default:
      return Bell;
  }
}

export function NotificationsPanel({ isOpen, onClose }: NotificationsPanelProps) {
  const { notifications, clearNotification, clearAll } = useNotifications();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-background border-l border-border shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-foreground" />
            <h2 className="text-base font-bold text-foreground">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear all
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-muted transition-colors">
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <CheckCircle className="w-10 h-10 text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground">All caught up!</p>
              <p className="text-xs text-muted-foreground mt-1">
                No pending pickups or deliveries
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {notifications.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <div
                    key={n.id}
                    className={`relative rounded-xl border p-3 bg-card ${
                      n.isOverdue ? 'border-destructive/40' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          n.isOverdue ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-foreground'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{n.title}</p>
                        <Link
                          to={`/wardrobe/item/${n.itemId}`}
                          onClick={onClose}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors block truncate"
                        >
                          {n.itemName}
                        </Link>
                        <p className="text-xs text-muted-foreground mt-0.5">{n.detail}</p>
                      </div>
                      <button
                        onClick={() => clearNotification(n.id)}
                        className="p-1 rounded-full hover:bg-muted transition-colors"
                        aria-label="Clear notification"
                      >
                        <X className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
